import XLSX from "xlsx";
import { groupBy, pick } from "lodash";
import { createRequestTypes } from "actions/requestTypes";
import { API_URL } from "../constants";

export const LOAD_WIND_TURBINE_PARAMS = createRequestTypes(
  "LOAD_WIND_TURBINE_PARAMS"
);

const sheetData = {};
// Same batching idea as sheetData above (many turbines sharing one xlsx url
// only trigger one fetch), keyed by schemaId instead of url, for turbine
// types that carry their register map + State/Failure text via
// TurbineSchema* tables (see scripts/lib/turbineSchemaXlsx.js) instead of a
// legacy per-type Excel.
const schemaData = {};

async function fetchJson(path) {
  const response = await fetch(`${API_URL}${path}`);
  return response.json();
}

// Schema-driven turbine types (type.schemaId set) get their State/Failure/
// parameter-scale data straight from the DB instead of fetching+parsing a
// raw xlsx client-side - the Excel for these types is uploaded once via
// Orbital-admin (see Orbital-server/routes/turbineTypes.js) and imported
// into TurbineSchema/TurbineSchemaRegister/TurbineSchemaState/
// TurbineSchemaFailure there. Dispatches the exact same
// LOAD_WIND_TURBINE_PARAMS.SUCCESS shape the legacy xlsx path uses (State/
// Failure by those exact key names - selectors/liveParams.js reads them
// unchanged) so nothing downstream needs to know which path fed it -
// selectors/utils.js's getTurbineParamsDefinitionByKey just scans every
// array under the response for a matching dbname regardless of key name, so
// a single "AllRegisters" bucket (input+holding rows, each already
// {dbname, scale, ...}) satisfies it. Command handling
// (LiveControlButtons.js/AdvancedControlButtons.js) already has its own
// independent schemaId branch and isn't affected by any of this.
const loadSchemaDrivenWindTurbineParams = (windTurbineId, type) => async (dispatch) => {
  const { schemaId } = type;

  if (!schemaData.hasOwnProperty(schemaId)) {
    schemaData[schemaId] = [];
  }
  schemaData[schemaId].push(windTurbineId);

  if (schemaData[schemaId].length > 1) {
    return;
  }

  const filter = encodeURIComponent(JSON.stringify({ schemaId }));
  const inputFilter = encodeURIComponent(JSON.stringify({ schemaId, registerType: "input" }));
  const holdingFilter = encodeURIComponent(JSON.stringify({ schemaId, registerType: "holding" }));

  const [states, failures, inputRegisters, holdingRegisters, schema] = await Promise.all([
    fetchJson(`/turbineSchemaStates?filter=${filter}`),
    fetchJson(`/turbineSchemaFailures?filter=${filter}`),
    fetchJson(`/turbineSchemaRegisters?filter=${inputFilter}`),
    fetchJson(`/turbineSchemaRegisters?filter=${holdingFilter}`),
    fetchJson(`/turbineSchemas/${schemaId}`),
  ]);

  // DI/DO come from TurbineSchema.digitalIO (a JSON blob, not its own
  // register-style table - see Orbital-server's database/init.sql) - reshape
  // each entry to the same {name, connector, pin, description, "value = 0",
  // "value = 1"} keys the legacy per-type xlsx path already produces, so
  // LiveControlsMenu.js's getDIDOFormattedData doesn't need a second code path.
  const toLegacyDigitalIOShape = (entries) => (entries || []).map((e) => ({
    // Bit position in the DI/DO word - NOT the entry's position in this list:
    // nordtank300 skips DI 02 and DI 21 (indices 1, 20), so positions and bits
    // differ from there on (2026-10-23: DI 23 showed DI 21's bit).
    index: e.index,
    name: e.name,
    connector: e.connector,
    pin: e.pin,
    description: e.description,
    'value = 0': e.valueOffLabel,
    'value = 1': e.valueOnLabel,
  }));
  const digitalIO = (schema && schema.digitalIO) || {};

  const response = {
    State: states,
    Failure: failures,
    AllRegisters: [...inputRegisters, ...holdingRegisters],
    DI: toLegacyDigitalIOShape(digitalIO.DI),
    DO: toLegacyDigitalIOShape(digitalIO.DO),
  };

  let windTurbine = null;
  while ((windTurbine = schemaData[schemaId].pop())) {
    dispatch({
      type: LOAD_WIND_TURBINE_PARAMS.SUCCESS,
      payload: {
        requestParams: { windTurbineId: windTurbine, type },
        response,
      },
    });
  }
};

export const loadWindTurbineParams = (windTurbineId, type) => (dispatch) => {
  if (!type) {
    return;
  }
  dispatch({
    type: LOAD_WIND_TURBINE_PARAMS.REQUEST,
    payload: { windTurbineId, type },
  });

  if (type.schemaId) {
    dispatch(loadSchemaDrivenWindTurbineParams(windTurbineId, type));
    return;
  }

  const { url } = type;

  if (!sheetData.hasOwnProperty(url)) {
    sheetData[url] = [];
  }
  sheetData[url].push(windTurbineId);

  if (sheetData[url].length > 1) {
    return;
  }

  const oReq = new XMLHttpRequest();
  oReq.responseType = "arraybuffer";

  oReq.addEventListener("load", () => {
    const arraybuffer = oReq.response;

    /* convert data to binary string */
    const data = new Uint8Array(arraybuffer);
    const arr = [];
    for (let i = 0; i !== data.length; ++i)
      arr[i] = String.fromCharCode(data[i]);
    const bstr = arr.join("");

    /* Call XLSX */
    const doc = XLSX.read(bstr, {
      type: "binary",
    });

    const sheets = [
      "Parameter",
      "Monitoring",
      "DI",
      "DO",
      "State",
      "Failure",
      "Command",
    ];
    let response = {};

    sheets.forEach((sheetName) => {
      const sheet = doc.Sheets[sheetName];

      const values = XLSX.utils.sheet_to_json(sheet, {
        raw: true,
      });

      const index = values.findIndex((v) => Object.keys(v).length > 3);
      const header = values[index];

      const allValues = values.slice(index + 1).map((p) => {
        const param = {};
        Object.keys(p).forEach((k) => {
          param[header[k].toLowerCase()] = p[k];
        });
        return param;
      });

      if (sheetName === "Monitoring") {
        response = {
          ...response,
          ...pick(groupBy(allValues, "catagori desc"), [
            "grid",
            "revolution",
            "revolutions",
            "vibration",
            "temperature",
            "wind",
            "meter",
            "condition",
            "io",
          ]),
        };
      } else {
        response[sheetName] = allValues;
      }
    });

    let windTurbine = null;
    while ((windTurbine = sheetData[url].pop())) {
      dispatch({
        type: LOAD_WIND_TURBINE_PARAMS.SUCCESS,
        payload: {
          requestParams: { windTurbineId: windTurbine, type },
          response,
        },
      });
    }
  });

  oReq.open("GET", url, true);
  oReq.send();
};
